import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

const HEIGHT_ANIMATION = { duration: 300, easing: 'cubic-bezier(0.2, 0, 0, 1)' };

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /**
   * Opt-in (data-animate-height="true"): animates the open/close height in JS.
   * The CSS ::details-content transition to `auto` needs interpolate-size, which
   * Safari and Firefox lack, so there the content just snaps.
   */
  get #animateHeight() {
    return this.dataset.animateHeight === 'true';
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  #closing = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (!this.#animateHeight || prefersReducedMotion()) return;

    event.preventDefault();
    const shouldOpen = !this.details.open || this.#closing;
    // Next frame, like the native toggle: later click handlers (e.g. analytics)
    // still see the state from before the click.
    requestAnimationFrame(() => (shouldOpen ? this.expand() : this.collapse()));
  };

  /**
   * Opens with a height animation. A `name` group's open siblings are closed
   * with the same animation; `name` is lifted meanwhile, or the browser would
   * close them instantly.
   */
  expand() {
    const { details } = this;
    const from = details.getBoundingClientRect().height;
    this.#stopAnimation();

    const name = details.getAttribute('name');
    const openSiblings = name
      ? Array.from(document.querySelectorAll(`details[name="${CSS.escape(name)}"][open]`)).filter(
          (other) => other !== details
        )
      : [];
    if (name && openSiblings.length) details.removeAttribute('name');

    details.open = true;
    const to = details.getBoundingClientRect().height;

    const closing = openSiblings.map((sibling) => {
      const accordion = sibling.closest('accordion-custom');
      if (accordion instanceof AccordionCustom) return accordion.collapse();
      sibling.open = false;
      return Promise.resolve();
    });

    this.#runHeightAnimation(from, to);

    if (name && openSiblings.length) {
      Promise.all(closing).then(() => details.setAttribute('name', name));
    }
  }

  /**
   * Closes with a height animation.
   * @returns {Promise<void>} Settles once closed or interrupted.
   */
  collapse() {
    const { details, summary } = this;
    if (!details.open) return Promise.resolve();

    const from = details.getBoundingClientRect().height;
    this.#stopAnimation();

    // Open-state styling (icons, summary spacing) keys off [open]:not([data-closing]),
    // so it flips at the click instead of 300 ms later. Set before measuring.
    details.dataset.closing = '';
    const style = getComputedStyle(details);
    const to =
      summary.getBoundingClientRect().height +
      parseFloat(style.paddingBlockStart) +
      parseFloat(style.paddingBlockEnd) +
      parseFloat(style.borderBlockStartWidth) +
      parseFloat(style.borderBlockEndWidth);

    this.#closing = true;

    return this.#runHeightAnimation(from, to, () => {
      details.open = false;
      this.#closing = false;
      delete details.dataset.closing;
    });
  }

  #stopAnimation() {
    this.#closing = false;
    delete this.details.dataset.closing;
    this.#animation?.cancel();
  }

  /**
   * @param {number} from - Start height in px.
   * @param {number} to - End height in px.
   * @param {() => void} [onFinish] - Runs on the last frame, before the fill is dropped.
   * @returns {Promise<void>}
   */
  #runHeightAnimation(from, to, onFinish) {
    const { details } = this;
    details.style.overflow = 'clip';

    const animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { ...HEIGHT_ANIMATION, fill: 'forwards' }
    );
    this.#animation = animation;

    return animation.finished.then(
      () => {
        onFinish?.();
        animation.cancel();
        this.#clearAnimation(animation);
      },
      () => this.#clearAnimation(animation)
    );
  }

  /**
   * @param {Animation} animation
   */
  #clearAnimation(animation) {
    if (this.#animation !== animation) return;

    this.#animation = null;
    this.details.style.overflow = '';
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
